import * as Styled from "./TableStatus.styles";

import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";

import { SearchBar } from "components/SearchBar/SearchBar";
import { Button, Switch } from "antd";
import { MenuGroupProps } from "utilities/getLocationDetails";
import TableStatusMappingButtons from "components/TableStatusMappingButtons/TableStatusMappingButtons";
import isDifferent from "utilities/isDifferent";
import { useLocation } from "context/LocationContext";
import { useParams } from "react-router-dom";
import { useTableStatus } from "context/TableStatusContext";

function TableStatus() {
  const [save, setSave] = useState(false);
  const { posGUID } = useParams();

  const [searchValue, setSearchValue] = useState("");
  const { locationDetails } = useLocation();

  const {
    tableStatusData,
    initialMappingState,
    isTableStatusMappingsSaving,
    isKdsEnabled,
    isKdsEnabledInitialState,
    handleIsKdsEnabled,
    mappingState,
    cancelChanges,
    updateMappingState,
    updateTableStatusMappings,
  } = useTableStatus();

  const tableStatusMap = tableStatusData?.tableStatusMap ?? {};

  const tableStatusMapArray = Object.keys(tableStatusMap).map((key) => {
    const item = tableStatusMap[key];

    return {
      name: item.name,
      key,
      item,
    };
  });

  // Search (filtered source)
  const filteredTableStatusMapArray = tableStatusMapArray.filter(
    (group) =>
      typeof group.name === "string" &&
      group.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  // number of items currently visible after search/filter
  const visibleItems = filteredTableStatusMapArray.length;

  // --- Pagination constants + state (client-side) ---
  const MAX_PER_PAGE = 100;
  const initialPageSize = Math.min(visibleItems || 10, MAX_PER_PAGE);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: initialPageSize,
    total: visibleItems,
  });

  // reset state on location changed
  useEffect(() => {
    if (posGUID) {
      setSave(false);

      // reset pagination when location changes
      setPagination({
        current: 1,
        pageSize: Math.min(tableStatusMapArray.length || 10, MAX_PER_PAGE),
        total: filteredTableStatusMapArray.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posGUID]);

  // Reset pagination when the visible (filtered) items change (e.g., search updates)
  useEffect(() => {
    const desiredPageSize = Math.min(visibleItems || 10, MAX_PER_PAGE);
    setPagination((prev) => {
      // if total visible count changed, reset to first page
      if (prev.total !== visibleItems) {
        return { current: 1, pageSize: desiredPageSize, total: visibleItems };
      }
      // if pageSize needs clamping (e.g., visibleItems dropped below previous pageSize)
      if (prev.pageSize !== desiredPageSize) {
        return { current: 1, pageSize: desiredPageSize, total: visibleItems };
      }
      return { ...prev, total: visibleItems };
    });
  }, [visibleItems]);

  useEffect(() => {
    const isMapDifferent =
      initialMappingState &&
      mappingState &&
      isDifferent(initialMappingState, mappingState);

    if (isMapDifferent) {
      setSave(true);
    } else {
      setSave(false);
    }
  }, [initialMappingState, mappingState]);

  useEffect(() => {
    if (isKdsEnabledInitialState !== isKdsEnabled) {
      setSave(true);
    } else {
      setSave(false);
    }
  }, [isKdsEnabledInitialState, isKdsEnabled]);

  const handleMapping = (menuGroupId: string, tableStatus: number) => {
    updateMappingState(menuGroupId, tableStatus);
  };

  const handleSave = useCallback(() => {
    updateTableStatusMappings();
  }, [updateTableStatusMappings]);

  const handleCancel = useCallback(() => {
    cancelChanges();
    setSave(false);
  }, [cancelChanges]);

  const handleChangeEnabled = useCallback(() => {
    handleIsKdsEnabled(!isKdsEnabled);
    setSave(true);
  }, [handleIsKdsEnabled, isKdsEnabled]);

  const columns = [
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>Menu Group</span>
          <SearchBar
            allowClear={true}
            placeholder="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
      ),
      dataIndex: "name",
      key: "name",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Mapping",
      key: "item",
      dataIndex: "item",
      render: (item: MenuGroupProps) => {
        return (
          <TableStatusMappingButtons
            item={item}
            mappingState={mappingState}
            onHandleMapping={handleMapping}
          />
        );
      },
    },
  ];

  // table change handler (AntD pagination object)
  const handleTableChange = (pg: any) => {
    const { current = 1, pageSize = pagination.pageSize } = pg;
    // ensure we clamp pageSize to MAX_PER_PAGE
    const effectivePageSize = Math.min(pageSize, MAX_PER_PAGE);
    setPagination({
      current,
      pageSize: effectivePageSize,
      total: visibleItems,
    });
  };

  return (
    <>
      <Styled.ButtonsRow>
        <Styled.SwitchContainer>
          KDS Enabled
          <Switch
            key={locationDetails?.locationId}
            checked={isKdsEnabled}
            onChange={handleChangeEnabled}
            title="KDS Enabled"
          />
        </Styled.SwitchContainer>
        <Button
          disabled={!save || isTableStatusMappingsSaving}
          loading={isTableStatusMappingsSaving}
          onClick={handleSave}
          type="primary"
          icon={<SaveOutlined />}
        >
          Save Changes
        </Button>
        <Button
          disabled={!save || isTableStatusMappingsSaving}
          onClick={handleCancel}
          type="default"
          icon={<CloseOutlined />}
        >
          Cancel
        </Button>
      </Styled.ButtonsRow>
      <Styled.Table
        key={locationDetails?.locationId}
        columns={columns}
        dataSource={filteredTableStatusMapArray}
        rowKey="key"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: visibleItems > MAX_PER_PAGE,
          pageSizeOptions: ["10", "20", "50", String(MAX_PER_PAGE)],
          hideOnSinglePage: true,
        }}
        onChange={handleTableChange}
      />
    </>
  );
}

export default TableStatus;
